import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SectionFieldCollection, SectionFieldDTO, FieldType } from '../magic-report.type';
import type { DynamicTableColumnDef, DynamicTableColumnType } from '../magic-report.type';
import { useMagicReport } from '../context/MagicReportContext';
import WhiteFab from '../../../common/WhiteFab';
import { Theme } from '@mui/material/styles';
import { Button } from 'components/index';
import { RESERVED_SECTION_TITLES } from './section-utils';
import { toast } from 'react-toastify';

const DYNAMIC_TABLE_COLUMNS_FIELD = 'dynamicTableColumns';
const DYNAMIC_TABLE_ROWS_FIELD = 'dynamicTableRows';

function slugify(text: string): string {
  return (
    text
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .slice(0, 40) || `col_${Date.now()}`
  );
}

/**
 * Creates a new empty dynamic section
 * @param header - Section header text
 * @param body - Section body text
 * @param tableColumns - Optional table column definitions; if provided, section will include an empty table
 * @param insertAfterSectionId - ID of the section to insert after (or 'end' for last position)
 */
const createNewDynamicSection = (
  header: string,
  body: string,
  tableColumns?: DynamicTableColumnDef[],
  insertAfterSectionId = 'end',
): SectionFieldCollection => {
  const fields: SectionFieldDTO[] = [
    {
      fieldName: 'header',
      fieldType: FieldType.Text,
      value: header,
    },
    {
      fieldName: 'body',
      fieldType: FieldType.Text,
      value: body,
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ];

  if (tableColumns && tableColumns.length > 0) {
    const emptyRow = {
      _id: `row-${Date.now()}`,
      ...tableColumns.reduce<Record<string, string | number>>((acc, col) => {
        if (col.type === 'numeric' || col.type === 'currency') {
          acc[col.id] = 0;
        } else {
          acc[col.id] = '';
        }
        return acc;
      }, {}),
    };

    fields.push(
      {
        fieldName: DYNAMIC_TABLE_COLUMNS_FIELD,
        fieldType: FieldType.Text,
        value: JSON.stringify(tableColumns),
      },
      {
        fieldName: DYNAMIC_TABLE_ROWS_FIELD,
        fieldType: FieldType.TextArray,
        entries: [
          {
            order: 0,
            value: JSON.stringify(emptyRow),
          },
        ],
      },
    );
  }

  return {
    fields,
    insertAfterSectionId: insertAfterSectionId === 'end' ? undefined : insertAfterSectionId,
  };
};

/**
 * DynamicSectionsManager - Manages user-created dynamic sections
 * Renders existing dynamic sections and exposes a floating action button
 * in the bottom-right corner that opens a dialog for creating a new section.
 */
const defaultTableColumn: DynamicTableColumnDef = { id: 'col_0', header: '', type: 'text' };

export const DynamicSectionsManager: React.FC = () => {
  const { reportState, setReportState, setIsDirty } = useMagicReport();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newHeader, setNewHeader] = useState('');
  const [newBody, setNewBody] = useState('');
  const [addTable, setAddTable] = useState(false);
  const [tableColumns, setTableColumns] = useState<DynamicTableColumnDef[]>([{ ...defaultTableColumn }]);

  // Get dynamic sections from state
  const dynamicSections = (reportState.sections as any)?.dynamicSections || [];

  // Initialize dynamicSections in state if sections exist but dynamicSections doesn't
  useEffect(() => {
    if (reportState.sections && !(reportState.sections as any)?.dynamicSections) {
      setReportState(prev => ({
        ...prev,
        sections: {
          ...(prev.sections || {}),
          dynamicSections: [],
        } as any,
      }));
    }
  }, [reportState.sections, setReportState]);

  // Build table columns to pass when "Add table" is checked
  const getTableColumnsForCreate = useCallback((): DynamicTableColumnDef[] | undefined => {
    if (!addTable || !tableColumns.length) return undefined;
    const valid = tableColumns
      .map((c, i) => ({
        id: (c.id || slugify(c.header) || `col_${i}`).trim() || `col_${i}`,
        header: (c.header || `Column ${i + 1}`).trim(),
        type: (c.type || 'text') as DynamicTableColumnType,
      }))
      .filter(c => c.header.length > 0);
    return valid.length > 0 ? valid : undefined;
  }, [addTable, tableColumns]);

  // Handle creating a new section
  const handleCreateSection = useCallback(() => {
    if (!newHeader.trim()) {
      toast.error('Please enter a section header');
      return;
    }

    const trimmedHeader = newHeader.trim();
    const normalizedHeader = trimmedHeader.toLowerCase();

    // Prevent duplicate section names (static or dynamic)
    const conflictsWithStatic = RESERVED_SECTION_TITLES.some(title => title.toLowerCase() === normalizedHeader);

    const conflictsWithDynamic = dynamicSections.some((section: SectionFieldCollection) => {
      const headerField = section.fields.find(f => f.fieldName === 'header');
      const value = (headerField?.value ?? '').toString().trim().toLowerCase();
      return value && value === normalizedHeader;
    });

    if (conflictsWithStatic || conflictsWithDynamic) {
      toast.error('A section with this name already exists. Please choose a different name.');
      return;
    }

    if (addTable && tableColumns.every(c => !(c.header || '').trim())) {
      toast.error('Add at least one table column with a name, or uncheck "Add a table".');
      return;
    }

    const tableCols = getTableColumnsForCreate();
    const newSection = createNewDynamicSection(trimmedHeader, newBody.trim(), tableCols);

    // Get the index for the new section (will be appended to end)
    const currentSections = (reportState.sections as any) || {};
    const existingDynamicSections = currentSections.dynamicSections || [];
    const newSectionIndex = existingDynamicSections.length;

    setReportState(prev => {
      const currentSections = (prev.sections as any) || {};
      const existingDynamicSections = currentSections.dynamicSections || [];
      return {
        ...prev,
        sections: {
          ...currentSections,
          dynamicSections: [...existingDynamicSections, newSection],
        } as any,
      };
    });

    setIsDirty(true);
    setNewHeader('');
    setNewBody('');
    setAddTable(false);
    setTableColumns([{ ...defaultTableColumn }]);
    setIsDialogOpen(false);

    // Scroll to the newly created section after DOM updates
    setTimeout(() => {
      const targetId = `dynamic-section-${newSectionIndex}`;
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }, [newHeader, newBody, addTable, tableColumns, getTableColumnsForCreate, setReportState, setIsDirty, reportState.sections]);

  // Handle deleting a section
  const handleDeleteSection = useCallback(
    (index: number) => {
      setReportState(prev => {
        const currentSections = (prev.sections as any) || {};
        const existingDynamicSections = currentSections.dynamicSections || [];
        const updatedDynamicSections = existingDynamicSections.filter((_: any, i: number) => i !== index);
        return {
          ...prev,
          sections: {
            ...currentSections,
            dynamicSections: updatedDynamicSections,
          } as any,
        };
      });
      setIsDirty(true);
    },
    [setReportState, setIsDirty],
  );

  // Handle updating a section
  const handleUpdateSection = useCallback(
    (index: number, updatedData: SectionFieldCollection) => {
      setReportState(prev => {
        const currentSections = (prev.sections as any) || {};
        const existingDynamicSections = currentSections.dynamicSections || [];
        const updatedDynamicSections = existingDynamicSections.map((section: SectionFieldCollection, i: number) =>
          i === index ? updatedData : section,
        );
        return {
          ...prev,
          sections: {
            ...currentSections,
            dynamicSections: updatedDynamicSections,
          } as any,
        };
      });
      setIsDirty(true);
    },
    [setReportState, setIsDirty],
  );

  // Handle canceling creation
  const handleCancelCreate = useCallback(() => {
    setNewHeader('');
    setNewBody('');
    setAddTable(false);
    setTableColumns([{ ...defaultTableColumn }]);
    setIsDialogOpen(false);
  }, []);

  const updateTableColumn = useCallback((index: number, updates: Partial<DynamicTableColumnDef>) => {
    setTableColumns(prev => {
      const next = [...prev];
      const current = next[index];
      // Don't update id from header here - it causes the input to remount and lose focus.
      // Final id is computed from header when creating the section (getTableColumnsForCreate).
      const { id: _omitId, ...rest } = updates;
      next[index] = { ...current, ...rest };
      return next;
    });
  }, []);

  const addTableColumn = useCallback(() => {
    setTableColumns(prev => [...prev, { id: `col_${prev.length}`, header: '', type: 'text' }]);
  }, []);

  const removeTableColumn = useCallback((index: number) => {
    setTableColumns(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next.map((col, i) => ({ ...col, id: col.id || `col_${i}` }));
    });
  }, []);

  return (
    <Box>
      {/* Existing Dynamic Sections - rendered in SectionsStep based on position */}
      {/* This component is now just for managing the sections, not rendering them */}

      {/* Create New Section Dialog */}
      <Dialog open={isDialogOpen} onClose={handleCancelCreate} fullWidth maxWidth='sm'>
        <DialogTitle>Add New Section</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label='Section Header'
              value={newHeader}
              onChange={e => setNewHeader(e.target.value.slice(0, 100))}
              fullWidth
              variant='outlined'
              size='small'
              placeholder='Enter section title...'
              required
              autoFocus
              inputProps={{ maxLength: 100 }}
              helperText={`${newHeader.length}/100 characters`}
            />
            <TextField
              label='Section Body'
              value={newBody}
              onChange={e => setNewBody(e.target.value)}
              fullWidth
              multiline
              rows={4}
              variant='outlined'
              size='small'
              placeholder='Enter section content...'
            />
            <FormControlLabel
              control={<Checkbox checked={addTable} onChange={e => setAddTable(e.target.checked)} color='primary' />}
              label='Add a table to this section'
            />
            {addTable && (
              <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Table columns (name and type)
                </Typography>
                {tableColumns.map((col, index) => (
                  <Box key={col.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                      label='Column name'
                      value={col.header}
                      onChange={e => updateTableColumn(index, { header: e.target.value })}
                      size='small'
                      placeholder='e.g. Item'
                      sx={{ flex: 1, minWidth: 160 }}
                    />
                    <FormControl size='small' sx={{ minWidth: 140 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={col.type || 'text'}
                        label='Type'
                        onChange={e => updateTableColumn(index, { type: e.target.value as DynamicTableColumnType })}
                      >
                        <MenuItem value='text'>Text</MenuItem>
                        <MenuItem value='currency'>Currency</MenuItem>
                        <MenuItem value='numeric'>Numeric</MenuItem>
                        <MenuItem value='date'>Date</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton size='small' onClick={() => removeTableColumn(index)} disabled={tableColumns.length <= 1} color='error'>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Box>
                ))}
                <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={addTableColumn}>
                  Add column
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={handleCancelCreate}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleCreateSection} disabled={!newHeader.trim()}>
            Create Section
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Add Button - bottom-right, above footer */}
      <Tooltip title='Add New Section' placement='left'>
        <WhiteFab
          icon={<AddIcon sx={{ fontSize: '28px !important' }} />}
          backgroundColor='#ffffff'
          textColor='primary'
          aria-label='add section'
          onClick={() => setIsDialogOpen(true)}
          sx={{
            position: 'fixed',
            bottom: (theme: Theme) => theme.spacing(16), // above footer buttons
            right: (theme: Theme) => theme.spacing(4),
            zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
            width: 64,
            height: 64,
          }}
        />
      </Tooltip>
    </Box>
  );
};
